// Employee Direct Allocation Import wizard state. A 3-step reducer (Upload -> Map
// Columns -> Preview), deliberately much simpler than SmartImport/wizardReducer.ts:
// there are only 4 fixed target fields (no dynamic section/field tree, no pending
// fields, no templates), and only two employee match bands (no "new"/"blank_name" —
// every row must resolve to an existing employee, per the approved plan).

export type ColumnTarget = 'employee' | 'percent' | 'customer' | 'class' | 'skip';

export interface ColumnMapping {
	colIndex: number;
	sourceHeader: string;
	target: ColumnTarget;
	sampleValues: string[];
}

export interface AllocationRowState {
	rowId: string;
	// Raw file text for Customer/Class, kept for display and re-resolution.
	customerText: string;
	classText: string;
	// Resolved QBO customer/class. Empty id + '-' name is the "blank" convention
	// EmployeeConfigModal already uses (rendered as "Blank Customer"/"Blank Class").
	customerId: string;
	customerName: string;
	classId: string;
	className: string;
	// true while the raw text is non-blank but couldn't be checked against
	// QuickBooks (option list unavailable) — blocks Import until resolved.
	customerReview: boolean;
	classReview: boolean;
	// The file named a Customer/Class that isn't in QuickBooks, so the row
	// defaulted to Blank. Doesn't block Import; shown so the user can pick one.
	customerUnmatched?: boolean;
	classUnmatched?: boolean;
	percent: number | null;
}

export interface EmployeeGroup {
	groupId: string;
	csvName: string;
	matchStatus: 'matched' | 'review';
	matchedEmployeeId: string | null;
	matchedEmployeeName: string | null;
	matchScore: number | null;
	rows: AllocationRowState[];
}

export interface WizardState {
	currentStep: 0 | 1 | 2;
	parseError: string | null;

	// Step 1
	file: File | null;
	sheets: Array<{ name: string; score: number }>;
	selectedSheet: string | null;
	headerRowIndex: number;
	rawMatrix: string[][];
	rawRows: Array<Record<string, unknown>>;

	// Step 2
	columnMappings: ColumnMapping[];

	// Step 3
	employeeGroups: EmployeeGroup[];
	// Template mode — the file has no Employee column, so its Customer/Class/%
	// lines are one allocation applied to employees the user picks in Step 3.
	templateGroup: EmployeeGroup | null;
	// The user's explicit choice of who gets the template — null until they
	// pick, which keeps Import disabled. 'selected' uses templateEmployeeIds.
	templateApplyTo: 'all' | 'selected' | null;
	templateEmployeeIds: string[];
}

/** groupId of the template "group" (shares row actions with employee groups). */
export const TEMPLATE_GROUP_ID = 'template';

export const initialState: WizardState = {
	currentStep: 0,
	parseError: null,
	file: null,
	sheets: [],
	selectedSheet: null,
	headerRowIndex: 0,
	rawMatrix: [],
	rawRows: [],
	columnMappings: [],
	employeeGroups: [],
	templateGroup: null,
	templateApplyTo: null,
	templateEmployeeIds: [],
};

export type WizardAction =
	| { type: 'FILE_SELECTED'; file: WizardState['file'] }
	| {
			type: 'PARSE_SUCCEEDED';
			payload: {
				sheets: WizardState['sheets'];
				selectedSheet: string | null;
				headerRowIndex: number;
				rawMatrix: string[][];
				rawRows: WizardState['rawRows'];
			};
	  }
	| { type: 'PARSE_FAILED'; error: string }
	// Step 2. Guarded like SmartImport's STEP2_INITIALIZED — only seeds when empty.
	| { type: 'COLUMN_MAPPINGS_INITIALIZED'; columnMappings: WizardState['columnMappings'] }
	// Sets `colIndex`'s target; if another column already holds that (non-skip)
	// target, it is bumped back to skip — at most one column per target.
	| { type: 'COLUMN_TARGET_CHANGED'; colIndex: number; target: ColumnTarget }
	// Step 3. Guarded like SmartImport's STEP3_INITIALIZED — only seeds when empty,
	// so Back-then-Next preserves the user's manual resolutions.
	| { type: 'GROUPS_INITIALIZED'; employeeGroups: WizardState['employeeGroups'] }
	| {
			type: 'GROUP_EMPLOYEE_OVERRIDDEN';
			payload: { groupId: string; employeeId: string; employeeName: string | null };
	  }
	| {
			type: 'ROW_CUSTOMER_CHANGED';
			payload: { groupId: string; rowId: string; customerId: string; customerName: string };
	  }
	| {
			type: 'ROW_CLASS_CHANGED';
			payload: { groupId: string; rowId: string; classId: string; className: string };
	  }
	| {
			type: 'ROW_PERCENT_CHANGED';
			payload: { groupId: string; rowId: string; percent: number | null };
	  }
	// Overwrites every row's percent in the given groups (employee or template).
	| { type: 'GROUP_PERCENTS_SET'; payload: Array<{ groupId: string; percents: number[] }> }
	// Template mode. Guarded like GROUPS_INITIALIZED.
	| { type: 'TEMPLATE_INITIALIZED'; templateGroup: EmployeeGroup }
	| { type: 'TEMPLATE_APPLY_TO_CHANGED'; applyTo: 'all' | 'selected' }
	| { type: 'TEMPLATE_EMPLOYEES_SELECTED'; employeeIds: string[] }
	| { type: 'ADVANCE_STEP' }
	| { type: 'BACK_STEP' }
	| { type: 'RESET' };

const clearedParseState = {
	parseError: null,
	sheets: [] as WizardState['sheets'],
	selectedSheet: null,
	headerRowIndex: 0,
	rawMatrix: [] as string[][],
	rawRows: [] as WizardState['rawRows'],
};

// Leaving Step 2 (or earlier) invalidates Step 3's grouping/resolution — column
// mappings may have changed, so the cached employeeGroups are stale. Mirrors
// SmartImport/wizardReducer.ts's step3ResetFor.
function groupsResetFor(currentStep: WizardState['currentStep']): Partial<WizardState> {
	return currentStep < 2
		? { employeeGroups: [], templateGroup: null, templateApplyTo: null, templateEmployeeIds: [] }
		: {};
}

// Applies `update` to the group with `groupId`, whether it's an employee group
// or the template, so the Customer/Class/% row actions serve both modes.
function updateGroup(
	state: WizardState,
	groupId: string,
	update: (g: EmployeeGroup) => EmployeeGroup
): WizardState {
	if (state.templateGroup?.groupId === groupId) {
		return { ...state, templateGroup: update(state.templateGroup) };
	}
	return {
		...state,
		employeeGroups: state.employeeGroups.map((g) => (g.groupId === groupId ? update(g) : g)),
	};
}

function updateRow(
	state: WizardState,
	groupId: string,
	rowId: string,
	update: (r: AllocationRowState) => AllocationRowState
): WizardState {
	return updateGroup(state, groupId, (g) => ({
		...g,
		rows: g.rows.map((r) => (r.rowId === rowId ? update(r) : r)),
	}));
}

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
	switch (action.type) {
		case 'FILE_SELECTED':
			return {
				...state,
				file: action.file,
				...clearedParseState,
				columnMappings: [],
				employeeGroups: [],
				templateGroup: null,
				templateApplyTo: null,
				templateEmployeeIds: [],
			};
		case 'PARSE_SUCCEEDED':
			return { ...state, parseError: null, ...action.payload };
		case 'PARSE_FAILED':
			return { ...state, ...clearedParseState, parseError: action.error };
		case 'COLUMN_MAPPINGS_INITIALIZED':
			if (state.columnMappings.length > 0) return state;
			return { ...state, columnMappings: action.columnMappings };
		case 'COLUMN_TARGET_CHANGED':
			return {
				...state,
				columnMappings: state.columnMappings.map((m): ColumnMapping => {
					if (m.colIndex === action.colIndex) return { ...m, target: action.target };
					if (action.target !== 'skip' && m.target === action.target) {
						return { ...m, target: 'skip' };
					}
					return m;
				}),
			};
		case 'GROUPS_INITIALIZED':
			if (state.employeeGroups.length > 0) return state;
			return { ...state, employeeGroups: action.employeeGroups };
		case 'GROUP_EMPLOYEE_OVERRIDDEN':
			return {
				...state,
				employeeGroups: state.employeeGroups.map((g) =>
					g.groupId === action.payload.groupId
						? {
								...g,
								matchStatus: 'matched',
								matchedEmployeeId: action.payload.employeeId,
								matchedEmployeeName: action.payload.employeeName,
						  }
						: g
				),
			};
		case 'ROW_CUSTOMER_CHANGED':
			return updateRow(state, action.payload.groupId, action.payload.rowId, (r) => ({
				...r,
				customerId: action.payload.customerId,
				customerName: action.payload.customerName || '-',
				customerReview: false,
				customerUnmatched: false,
			}));
		case 'ROW_CLASS_CHANGED':
			return updateRow(state, action.payload.groupId, action.payload.rowId, (r) => ({
				...r,
				classId: action.payload.classId,
				className: action.payload.className || '-',
				classReview: false,
				classUnmatched: false,
			}));
		case 'ROW_PERCENT_CHANGED':
			return updateRow(state, action.payload.groupId, action.payload.rowId, (r) => ({
				...r,
				percent: action.payload.percent,
			}));
		case 'GROUP_PERCENTS_SET':
			return action.payload.reduce(
				(next, { groupId, percents }) =>
					updateGroup(next, groupId, (g) => ({
						...g,
						rows: g.rows.map((r, i) => ({ ...r, percent: percents[i] ?? r.percent })),
					})),
				state
			);
		case 'TEMPLATE_INITIALIZED':
			if (state.templateGroup) return state;
			return { ...state, templateGroup: action.templateGroup };
		case 'TEMPLATE_APPLY_TO_CHANGED':
			return { ...state, templateApplyTo: action.applyTo };
		case 'TEMPLATE_EMPLOYEES_SELECTED':
			return { ...state, templateEmployeeIds: action.employeeIds };
		case 'ADVANCE_STEP':
			return {
				...state,
				currentStep: Math.min(2, state.currentStep + 1) as WizardState['currentStep'],
			};
		case 'BACK_STEP': {
			const currentStep = Math.max(0, state.currentStep - 1) as WizardState['currentStep'];
			return { ...state, currentStep, ...groupsResetFor(currentStep) };
		}
		case 'RESET':
			return initialState;
		default:
			return state;
	}
}
